// ============================================
// SpriteBatch - Efficient sprite batching system
// ============================================

import { type IRenderContext } from '../core/IRenderContext';
import { type Camera2D } from '../objects/Camera2D';
import type { LightManager } from '../lighting/LightManager';
import type { LightCullPass } from '../lighting/LightCullPass';
import {
  type TextureHandle,
  type BufferHandle,
  type PipelineHandle,
  type ShaderHandle,
  BlendMode,
  SPRITE_VERTEX_SIZE,
  SPRITE_VERTEX_LAYOUT,
  NULL_HANDLE,
  blendStateFor,
} from '../types';
import type { Material } from '../pipeline/Material';

// Import WGSL shader
import { getCompiledShader } from '../pipeline/ShaderCache';
import { toHalf } from '../core/half';

// Batch constants
const DEFAULT_MAX_SPRITES = 10000;
const VERTICES_PER_SPRITE = 4;
const INDICES_PER_SPRITE = 6;

/**
 * High-performance sprite batching
 * Collects sprites and renders them in batches, minimizing draw calls
 */
export class SpriteBatch {
  private ctx: IRenderContext;
  private maxSprites: number;
  
  // GPU resources
  private vertexBuffer: BufferHandle = NULL_HANDLE;
  private indexBuffer: BufferHandle = NULL_HANDLE;
  private shader: ShaderHandle = NULL_HANDLE;
  private pipelineLDR: PipelineHandle = NULL_HANDLE; // For swap chain (bgra8unorm)
  private pipelineHDR: PipelineHandle = NULL_HANDLE; // For HDR render targets (rgba16float)
  private currentPipeline: PipelineHandle = NULL_HANDLE;
  /** Whether the active batch targets an HDR (rgba16float) buffer — picks the matching material pipeline. */
  private currentHdrTarget = false;

  // GBuffer (deferred) pipeline — lazy initialized
  private shaderGBuffer: ShaderHandle = NULL_HANDLE;
  private pipelineGBuffer: PipelineHandle = NULL_HANDLE;

  // Forward-lit pipeline — lazy initialized
  private shaderForwardLit: ShaderHandle = NULL_HANDLE;
  private pipelineForwardLitLDR: PipelineHandle = NULL_HANDLE;
  private pipelineForwardLitHDR: PipelineHandle = NULL_HANDLE;

  // Forward+ pipeline — lazy initialized
  private pipelineForwardPlusLDR: PipelineHandle = NULL_HANDLE;
  private pipelineForwardPlusHDR: PipelineHandle = NULL_HANDLE;

  /** Non-Normal blend variants of the unlit color pipelines, keyed `${ldr|hdr}|${mode}`. Built on demand. */
  private pipelineVariants = new Map<string, PipelineHandle>();
  /** One-shot guard so an unsupported setBlendMode on a lit/GBuffer batch warns only once. */
  private _blendVariantWarned = false;
  
  // Vertex data (CPU-side). Positions/UVs are written as f32; color + emission as f16 through the
  // u16 view (same underlying buffer).
  private vertexData: Float32Array;
  private vertexDataU16: Uint16Array;
  
  // Current batch state
  private spriteCount = 0;
  private batchStartSprite = 0;  // Where current batch starts in buffer
  private currentTexture: TextureHandle = NULL_HANDLE;
  private currentBlendMode: BlendMode = 1; // Normal
  private currentMaterial: Material | null = null;
  private batching = false;
  private camera: Camera2D | null = null;
  /** Animation clock (seconds) fed to material shaders via the `uTime` uniform. */
  private elapsedTime = 0;
  
  // Stats
  private _drawCalls = 0;
  private _spriteCount = 0;
  
  // View-projection matrix
  private vpMatrix = new Float32Array(16);
  
  /**
   * Reset buffer cursor to the beginning. Must be called once per frame
   * BEFORE any begin()/end() cycles to reclaim the vertex buffer.
   *
   * In WebGPU, all queue.writeBuffer() calls execute before the
   * render pass, so multiple begin/end cycles that write to the same
   * buffer region would overwrite earlier data. startFrame() ensures
   * each frame starts fresh at offset 0.
   */
  startFrame(): void {
    this.spriteCount = 0;
    this.batchStartSprite = 0;
  }
  
  constructor(ctx: IRenderContext, options?: { maxSprites?: number }) {
    this.ctx = ctx;
    this.maxSprites = options?.maxSprites ?? DEFAULT_MAX_SPRITES;
    
    // Allocate vertex data buffer
    const vertexDataSize = this.maxSprites * VERTICES_PER_SPRITE * SPRITE_VERTEX_SIZE;
    const buffer = new ArrayBuffer(vertexDataSize);
    this.vertexData = new Float32Array(buffer);
    this.vertexDataU16 = new Uint16Array(buffer);
    
    this.initResources();
  }
  
  private initResources(): void {
    const { ctx } = this;
    
    // Sprite shader (Slang → WGSL, compiled into ShaderCache at engine startup).
    const spriteWgslSource = getCompiledShader('sprite');
    this.shader = ctx.createShader(spriteWgslSource, spriteWgslSource);
    
    // Create LDR pipeline for swap chain rendering
    this.pipelineLDR = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [SPRITE_VERTEX_LAYOUT],
      colorTargets: [{ format: ctx.getSwapChainFormat() }],
      label: 'SpriteBatch-ldr',
    });
    
    // Create HDR pipeline for offscreen HDR render targets
    this.pipelineHDR = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [SPRITE_VERTEX_LAYOUT],
      colorTargets: [{ format: 'rgba16float' }],
      label: 'SpriteBatch-hdr',
    });
    
    // Start with LDR pipeline
    this.currentPipeline = this.pipelineLDR;
    
    // Create vertex buffer
    this.vertexBuffer = ctx.createBuffer({
      size: this.maxSprites * VERTICES_PER_SPRITE * SPRITE_VERTEX_SIZE,
      usage: ['vertex'],
      label: 'SpriteBatch-vb',
    });
    
    // Create and populate index buffer
    const indices = new Uint16Array(this.maxSprites * INDICES_PER_SPRITE);
    for (let i = 0; i < this.maxSprites; i++) {
      const vertexOffset = i * 4;
      const indexOffset = i * 6;
      // Two triangles forming a quad
      indices[indexOffset + 0] = vertexOffset + 0;
      indices[indexOffset + 1] = vertexOffset + 1;
      indices[indexOffset + 2] = vertexOffset + 2;
      indices[indexOffset + 3] = vertexOffset + 2;
      indices[indexOffset + 4] = vertexOffset + 3;
      indices[indexOffset + 5] = vertexOffset + 0;
    }
    
    this.indexBuffer = ctx.createBuffer({
      size: indices.byteLength,
      usage: ['index'],
      data: indices,
      label: 'SpriteBatch-ib',
    });
  }
  
  /**
   * Begin a sprite batch targeting the GBuffer (deferred lighting path).
   * Sprites will be rendered to MRT: albedo + normal + emission.
   * Must have GBuffer set as active MRT render target before calling.
   * @param camera - Camera to use for view-projection matrix
   */
  beginGBuffer(camera: Camera2D): void {
    if (this.batching) {
      throw new Error('SpriteBatch.beginGBuffer() called without ending previous batch');
    }

    // Lazy-init GBuffer pipeline on first use
    if (this.pipelineGBuffer === NULL_HANDLE) {
      this.initGBufferPipeline();
    }

    this.batching = true;
    this.camera = camera;
    // Don't reset spriteCount — continue from where the previous batch left off
    // to avoid WebGPU buffer overwrites when multiple begin/end cycles per frame
    this.batchStartSprite = this.spriteCount;
    this._drawCalls = 0;
    this._spriteCount = 0;
    this.currentTexture = NULL_HANDLE;
    this.currentMaterial = null;

    // Use GBuffer pipeline (3 MRT targets)
    this.currentPipeline = this.pipelineGBuffer;

    // Get view-projection matrix
    camera.getViewProjection(this.vpMatrix);
  }

  /**
   * Begin a sprite batch with forward lighting (inline per-fragment lighting).
   * Sprites are lit directly in the fragment shader — no GBuffer or separate
   * accumulation pass. Used for transparent sprites that can't go through deferred.
   *
   * The caller must have already called `lightManager.end()` to upload light data.
   *
   * @param camera - Camera to use for view-projection matrix
   * @param lightManager - LightManager with uploaded light data
   * @param hdrTarget - Whether rendering to HDR target (rgba16float) or swap chain
   */
  beginForwardLit(camera: Camera2D, lightManager: LightManager, hdrTarget = false): void {
    if (this.batching) {
      throw new Error('SpriteBatch.beginForwardLit() called without ending previous batch');
    }

    // Lazy-init forward-lit pipelines on first use
    if (this.pipelineForwardLitLDR === NULL_HANDLE) {
      this.initForwardLitPipeline();
    }

    this.batching = true;
    this.camera = camera;
    // Don't reset spriteCount — continue from where the previous batch left off
    this.batchStartSprite = this.spriteCount;
    this._drawCalls = 0;
    this._spriteCount = 0;
    this.currentTexture = NULL_HANDLE;
    this.currentMaterial = null;

    // Select LDR or HDR forward-lit pipeline
    this.currentHdrTarget = hdrTarget;
    this.currentPipeline = hdrTarget ? this.pipelineForwardLitHDR : this.pipelineForwardLitLDR;

    // Bind lighting data (group 1) so the forward-lit shader can access lights
    this.ctx.setLightingData!(lightManager.uniformBuffer, lightManager.lightBuffer);

    // Get view-projection matrix
    camera.getViewProjection(this.vpMatrix);
  }

  /**
   * Begin a Forward+ lit sprite batch.
   * Uses per-tile culled light lists from a LightCullPass compute dispatch.
   * Must be called after lightCullPass.cull().
   *
   * @param camera - Camera to use for view-projection matrix
   * @param lightManager - LightManager with uploaded light data
   * @param lightCullPass - LightCullPass that has already run cull() this frame
   * @param hdrTarget - Whether rendering to HDR target (rgba16float) or swap chain
   * @param shadowMapBuffer - Optional shadow map buffer from ShadowMapPass
   */
  beginForwardPlus(camera: Camera2D, lightManager: LightManager, lightCullPass: LightCullPass, hdrTarget = false, shadowMapBuffer?: BufferHandle, iesProfileBuffer?: BufferHandle): void {
    if (this.batching) {
      throw new Error('SpriteBatch.beginForwardPlus() called without ending previous batch');
    }

    // Lazy-init Forward+ pipelines on first use
    if (this.pipelineForwardPlusLDR === NULL_HANDLE) {
      this.initForwardPlusPipeline();
    }

    this.batching = true;
    this.camera = camera;
    // Don't reset spriteCount — continue from where the previous batch left off
    this.batchStartSprite = this.spriteCount;
    this._drawCalls = 0;
    this._spriteCount = 0;
    this.currentTexture = NULL_HANDLE;
    this.currentMaterial = null;

    // Select LDR or HDR Forward+ pipeline
    this.currentHdrTarget = hdrTarget;
    this.currentPipeline = hdrTarget ? this.pipelineForwardPlusHDR : this.pipelineForwardPlusLDR;

    // Bind Forward+ data (group 1): uniforms + lights + tile light indices + shadows + IES
    this.ctx.setForwardPlusData!(
      lightManager.uniformBuffer,
      lightManager.lightBuffer,
      lightCullPass.tileLightDataBuffer,
      shadowMapBuffer,
      iesProfileBuffer,
    );

    // Get view-projection matrix
    camera.getViewProjection(this.vpMatrix);
  }

  /** Set the animation clock (seconds) fed to material shaders via `uTime`. Call once per frame. */
  setTime(seconds: number): void {
    this.elapsedTime = seconds;
  }

  /**
   * Begin a sprite batch
   * @param camera - Camera to use for view-projection matrix
   * @param hdrTarget - Whether rendering to HDR target (rgba16float) or swap chain
   */
  begin(camera: Camera2D, hdrTarget = false): void {
    if (this.batching) {
      throw new Error('SpriteBatch.begin() called without ending previous batch');
    }
    
    this.batching = true;
    this.camera = camera;
    // Don't reset spriteCount — continue from where the previous batch left off
    // to avoid WebGPU buffer overwrites when multiple begin/end cycles per frame
    this.batchStartSprite = this.spriteCount;
    this._drawCalls = 0;
    this._spriteCount = 0;
    this.currentTexture = NULL_HANDLE;
    this.currentMaterial = null;
    
    // Select appropriate pipeline based on render target
    this.currentHdrTarget = hdrTarget;
    this.currentPipeline = hdrTarget ? this.pipelineHDR : this.pipelineLDR;
    
    // Get view-projection matrix
    camera.getViewProjection(this.vpMatrix);
  }
  
  /**
   * End batch and render all sprites
   */
  end(): void {
    if (!this.batching) {
      throw new Error('SpriteBatch.end() called without begin()');
    }
    
    // Flush any remaining sprites
    this.flush();
    
    this.batching = false;
    this.camera = null;
  }

  /**
   * Break the current batch when the next quad can't share it — a change of material,
   * texture, or a full buffer. flush() consumes the OLD state, so the new state is set
   * only after flushing.
   *
   * Passing material=null selects the built-in sprite pipeline in flush(). This is the
   * fix for the "default sprite shows a random material" bug: the plain draw paths used
   * to leave currentMaterial untouched, so a default sprite drawn after a material sprite
   * inherited that material. Now every path funnels through here with an explicit material.
   */
  private breakBatchIfNeeded(material: Material | null, texture: TextureHandle): void {
    if (
      material !== this.currentMaterial ||
      texture !== this.currentTexture ||
      this.spriteCount >= this.maxSprites
    ) {
      this.flush();
      this.currentMaterial = material;
      this.currentTexture = texture;
    }
  }

  /**
   * Draw a textured sprite
   */
  draw(
    texture: TextureHandle,
    x: number,
    y: number,
    width: number,
    height: number,
    u0 = 0,
    v0 = 0,
    u1 = 1,
    v1 = 1,
    tintR = 1,
    tintG = 1,
    tintB = 1,
    tintA = 1,
    emissionR = 0,
    emissionG = 0,
    emissionB = 0,
    emissionA = 0
  ): void {
    if (!this.batching) {
      throw new Error('Cannot draw sprite - call begin() first');
    }

    this.breakBatchIfNeeded(null, texture);

    // Write vertex data
    this.writeQuad(x, y, width, height, u0, v0, u1, v1, tintR, tintG, tintB, tintA, emissionR, emissionG, emissionB, emissionA);
  }
  
  /**
   * Draw a rotated sprite
   */
  drawRotated(
    texture: TextureHandle,
    x: number,
    y: number,
    width: number,
    height: number,
    rotation: number,
    anchorX = 0.5,
    anchorY = 0.5,
    u0 = 0,
    v0 = 0,
    u1 = 1,
    v1 = 1,
    tintR = 1,
    tintG = 1,
    tintB = 1,
    tintA = 1,
    emissionR = 0,
    emissionG = 0,
    emissionB = 0,
    emissionA = 0
  ): void {
    if (!this.batching) {
      throw new Error('Cannot draw sprite - call begin() first');
    }

    this.breakBatchIfNeeded(null, texture);

    // Write rotated quad
    this.writeQuadRotated(x, y, width, height, rotation, anchorX, anchorY, u0, v0, u1, v1, tintR, tintG, tintB, tintA, emissionR, emissionG, emissionB, emissionA);
  }
  
  /**
   * Draw a tiled/repeating texture
   * texWidth/texHeight are the dimensions of the texture for UV calculation
   * offsetX/offsetY shift the tiling pattern
   */
  drawTiled(
    texture: TextureHandle,
    x: number,
    y: number,
    width: number,
    height: number,
    texWidth: number,
    texHeight: number,
    offsetX = 0,
    offsetY = 0,
    tintR = 1,
    tintG = 1,
    tintB = 1,
    tintA = 1,
    emissionR = 0,
    emissionG = 0,
    emissionB = 0,
    emissionA = 0
  ): void {
    if (!this.batching) {
      throw new Error('Cannot draw sprite - call begin() first');
    }

    this.breakBatchIfNeeded(null, texture);

    // Calculate UVs for tiling
    const u0 = offsetX / texWidth;
    const v0 = offsetY / texHeight;
    const u1 = (offsetX + width) / texWidth;
    const v1 = (offsetY + height) / texHeight;
    
    this.writeQuad(x, y, width, height, u0, v0, u1, v1, tintR, tintG, tintB, tintA);
  }
  
  /**
   * Draw a quad with custom vertex positions (for shear/iso/hex transforms)
   * Vertices are: v0=top-left, v1=top-right, v2=bottom-right, v3=bottom-left
   */
  drawQuadVerts(
    texture: TextureHandle,
    v0x: number, v0y: number,
    v1x: number, v1y: number,
    v2x: number, v2y: number,
    v3x: number, v3y: number,
    u0 = 0,
    v0 = 0,
    u1 = 1,
    v1 = 1,
    tintR = 1,
    tintG = 1,
    tintB = 1,
    tintA = 1,
    emissionR = 0,
    emissionG = 0,
    emissionB = 0,
    emissionA = 0
  ): void {
    if (!this.batching) {
      throw new Error('Cannot draw sprite - call begin() first');
    }

    this.breakBatchIfNeeded(null, texture);

    this.writeQuadVerts(v0x, v0y, v1x, v1y, v2x, v2y, v3x, v3y, u0, v0, u1, v1, tintR, tintG, tintB, tintA, emissionR, emissionG, emissionB, emissionA);
  }
  
  // -----------------------------------------------------------------------
  // Material-based drawing
  // -----------------------------------------------------------------------
  
  /**
   * Draw a sprite using a Material.
   * Flushes on material change (not just texture change).
   * The material's pipeline, textures and uniforms are applied on flush.
   *
   * When using drawWithMaterial, do NOT mix with plain draw() calls
   * in the same begin/end block.
   */
  drawWithMaterial(
    material: Material,
    x: number,
    y: number,
    width: number,
    height: number,
    u0 = 0,
    v0 = 0,
    u1 = 1,
    v1 = 1,
    tintR = 1,
    tintG = 1,
    tintB = 1,
    tintA = 1,
    /** Sprite texture to bind as ALBEDO for this quad. Falls back to the material's own ALBEDO. */
    albedoTexture?: TextureHandle,
    emissionR = 0,
    emissionG = 0,
    emissionB = 0,
    emissionA = 0
  ): void {
    if (!this.batching) {
      throw new Error('Cannot draw sprite - call begin() first');
    }

    // Break on (material, albedo texture) so sprites sharing one .mat but different
    // textures each bind their own texture — flush() re-applies currentTexture as ALBEDO.
    this.breakBatchIfNeeded(material, albedoTexture ?? material.getTexture('ALBEDO'));

    this.writeQuad(x, y, width, height, u0, v0, u1, v1, tintR, tintG, tintB, tintA, emissionR, emissionG, emissionB, emissionA);
  }
  
  /**
   * Draw a rotated sprite using a Material.
   */
  drawRotatedWithMaterial(
    material: Material,
    x: number,
    y: number,
    width: number,
    height: number,
    rotation: number,
    anchorX = 0.5,
    anchorY = 0.5,
    u0 = 0,
    v0 = 0,
    u1 = 1,
    v1 = 1,
    tintR = 1,
    tintG = 1,
    tintB = 1,
    tintA = 1,
    /** Sprite texture to bind as ALBEDO for this quad. Falls back to the material's own ALBEDO. */
    albedoTexture?: TextureHandle,
    emissionR = 0,
    emissionG = 0,
    emissionB = 0,
    emissionA = 0
  ): void {
    if (!this.batching) {
      throw new Error('Cannot draw sprite - call begin() first');
    }

    this.breakBatchIfNeeded(material, albedoTexture ?? material.getTexture('ALBEDO'));

    this.writeQuadRotated(x, y, width, height, rotation, anchorX, anchorY, u0, v0, u1, v1, tintR, tintG, tintB, tintA, emissionR, emissionG, emissionB, emissionA);
  }
  
  /**
   * Set the blend mode for subsequent (plain, unlit) draws. Selects a pipeline variant with the
   * matching blend state — the old implementation called ctx.setBlendMode(), a no-op, so blend
   * modes never took effect. Only the unlit color pipelines (begin()) support this; lit / GBuffer
   * batches must not additive-blend (it corrupts lighting math / the MRT), so a non-Normal mode is
   * ignored there with a one-time warning. Materials carry their own blend baked into their pipeline.
   */
  setBlendMode(mode: BlendMode): void {
    if (mode === this.currentBlendMode) return;
    this.flush();
    this.currentBlendMode = mode;

    const base =
      this.currentPipeline === this.pipelineLDR ? 'ldr' :
      this.currentPipeline === this.pipelineHDR ? 'hdr' : null;

    if (base === null) {
      if (mode !== BlendMode.Normal && !this._blendVariantWarned) {
        this._blendVariantWarned = true;
        console.warn('[SpriteBatch] setBlendMode only applies to the unlit sprite pipeline; ignoring for lit/GBuffer batches.');
      }
      return;
    }

    this.currentPipeline =
      mode === BlendMode.Normal
        ? (base === 'hdr' ? this.pipelineHDR : this.pipelineLDR)
        : this.getBlendVariant(base, mode);
  }

  /** Build (and cache) an unlit color pipeline whose color target uses the given blend mode. */
  private getBlendVariant(base: 'ldr' | 'hdr', mode: BlendMode): PipelineHandle {
    const key = `${base}|${mode}`;
    let p = this.pipelineVariants.get(key);
    if (p === undefined) {
      const format = base === 'hdr' ? 'rgba16float' : this.ctx.getSwapChainFormat();
      p = this.ctx.createPipeline({
        shader: this.shader,
        vertexBuffers: [SPRITE_VERTEX_LAYOUT],
        colorTargets: [{ format, blend: blendStateFor(mode) }],
        label: `SpriteBatch-${base}-b${mode}`,
      });
      this.pipelineVariants.set(key, p);
    }
    return p;
  }
  
  /**
   * Flush current batch to GPU
   */
  private flush(): void {
    const spritesInBatch = this.spriteCount - this.batchStartSprite;
    if (spritesInBatch === 0) return;
    
    const indexCount = spritesInBatch * INDICES_PER_SPRITE;
    const firstIndex = this.batchStartSprite * INDICES_PER_SPRITE;
    
    const { ctx } = this;
    
    // Upload only this batch's vertex data at the correct buffer offset
    const batchByteOffset = this.batchStartSprite * VERTICES_PER_SPRITE * SPRITE_VERTEX_SIZE;
    const floatsPerVertex = SPRITE_VERTEX_SIZE / 4;
    const batchFloatCount = spritesInBatch * VERTICES_PER_SPRITE * floatsPerVertex;
    const batchFloatStart = batchByteOffset / 4;
    // Use subarray() to avoid allocating new Float32Array wrapper objects
    const dataToUpload = this.vertexData.subarray(batchFloatStart, batchFloatStart + batchFloatCount);
    ctx.updateBuffer(this.vertexBuffer, dataToUpload, batchByteOffset);
    
    if (this.currentMaterial) {
      // Material path. ORDER MATTERS for per-draw uniform isolation: write ALL uniforms (the material's
      // properties via bindPipeline + VP/time) → snapshot them → THEN bind textures (the texture bind group
      // captures the uniform buffer at creation). Without this, every material draw shares one uniform
      // buffer and reads the last write (queue.writeBuffer lands before the pass runs) — colors bleed.
      // Bind through THIS batch's ctx (the one with the active render pass) — a material may have been built
      // on a different (shared-resource) context, e.g. editor-built material drawn by the GameView's ctx.
      this.currentMaterial.bindPipeline(this.currentHdrTarget, ctx);
      // Bind THIS batch's sprite texture as ALBEDO, right before bindTextures reads it.
      // Sprites sharing one cached .mat but different textures each get their own texture,
      // instead of all inheriting whichever sprite mutated the shared material last.
      if (this.currentTexture !== NULL_HANDLE) {
        this.currentMaterial.setTexture('ALBEDO', this.currentTexture);
      }
      ctx.setUniform('uViewProjection', this.vpMatrix);
      ctx.setUniform('uTime', this.elapsedTime); // no-op unless the material's uniform layout has uTime
      ctx.captureUniforms?.();
      this.currentMaterial.bindTextures(ctx);
    } else {
      // Default path: use built-in sprite pipeline
      ctx.setPipeline(this.currentPipeline);
      ctx.bindTexture(0, this.currentTexture);
      // Note: WebGPU handles texture bindings via bind group, no setUniformInt needed
      ctx.setUniform('uViewProjection', this.vpMatrix);
    }
    
    // Bind buffers and set up attributes
    ctx.bindVertexBuffer(this.vertexBuffer);
    ctx.setupVertexAttributes(SPRITE_VERTEX_LAYOUT);
    ctx.bindIndexBuffer(this.indexBuffer);
    
    // Draw with offset into the index buffer
    ctx.drawIndexed(indexCount, firstIndex);
    
    this._drawCalls++;
    this._spriteCount += spritesInBatch;
    
    // If the buffer is full, recycle from position 0 to avoid overflow
    if (this.spriteCount >= this.maxSprites) {
      this.spriteCount = 0;
      this.batchStartSprite = 0;
    } else {
      this.batchStartSprite = this.spriteCount;
    }
  }
  
  /**
   * Write color (f16x4, float slots 4-5) + emission (f16x4, float slots 6-7) for the vertex whose
   * position/uv were just written at float index `i`. Returns the next vertex's base float index.
   * Halves are precomputed once per quad by the callers.
   */
  private writeColorEmission(
    i: number,
    rh: number, gh: number, bh: number, ah: number,
    erh: number, egh: number, ebh: number, eah: number,
  ): number {
    const u16 = this.vertexDataU16;
    let o = i * 2;
    u16[o++] = rh;
    u16[o++] = gh;
    u16[o++] = bh;
    u16[o++] = ah;
    u16[o++] = erh;
    u16[o++] = egh;
    u16[o++] = ebh;
    u16[o++] = eah;
    return i + 4; // color + emission consume 4 f32 slots
  }

  /**
   * Write a non-rotated quad to vertex buffer
   */
  private writeQuad(
    x: number,
    y: number,
    w: number,
    h: number,
    u0: number,
    v0: number,
    u1: number,
    v1: number,
    r: number,
    g: number,
    b: number,
    a: number,
    er = 0,
    eg = 0,
    eb = 0,
    ea = 0
  ): void {
    const floatsPerVertex = SPRITE_VERTEX_SIZE / 4;
    const baseIndex = this.spriteCount * VERTICES_PER_SPRITE * floatsPerVertex;
    const data = this.vertexData;

    // Pack color + emission as halves, once per quad (HDR: values may exceed 1)
    const rh = toHalf(r);
    const gh = toHalf(g);
    const bh = toHalf(b);
    const ah = toHalf(a);
    const erh = toHalf(er);
    const egh = toHalf(eg);
    const ebh = toHalf(eb);
    const eah = toHalf(ea);

    // Vertex 0: top-left
    let i = baseIndex;
    data[i++] = x;       // position x
    data[i++] = y;       // position y
    data[i++] = u0;      // uv u
    data[i++] = v0;      // uv v
    i = this.writeColorEmission(i, rh, gh, bh, ah, erh, egh, ebh, eah);

    // Vertex 1: top-right
    data[i++] = x + w;
    data[i++] = y;
    data[i++] = u1;
    data[i++] = v0;
    i = this.writeColorEmission(i, rh, gh, bh, ah, erh, egh, ebh, eah);

    // Vertex 2: bottom-right
    data[i++] = x + w;
    data[i++] = y + h;
    data[i++] = u1;
    data[i++] = v1;
    i = this.writeColorEmission(i, rh, gh, bh, ah, erh, egh, ebh, eah);

    // Vertex 3: bottom-left
    data[i++] = x;
    data[i++] = y + h;
    data[i++] = u0;
    data[i++] = v1;
    this.writeColorEmission(i, rh, gh, bh, ah, erh, egh, ebh, eah);

    this.spriteCount++;
  }
  
  /**
   * Write a rotated quad to vertex buffer
   */
  private writeQuadRotated(
    x: number,
    y: number,
    w: number,
    h: number,
    rotation: number,
    anchorX: number,
    anchorY: number,
    u0: number,
    v0: number,
    u1: number,
    v1: number,
    r: number,
    g: number,
    b: number,
    a: number,
    er = 0,
    eg = 0,
    eb = 0,
    ea = 0
  ): void {
    const floatsPerVertex = SPRITE_VERTEX_SIZE / 4;
    const baseIndex = this.spriteCount * VERTICES_PER_SPRITE * floatsPerVertex;
    const data = this.vertexData;

    const cos = Math.cos(rotation);
    const sin = Math.sin(rotation);

    // Anchor offset
    const ax = w * anchorX;
    const ay = h * anchorY;

    // Transform function
    const tx = (px: number, py: number) => {
      const lx = px - ax;
      const ly = py - ay;
      return x + ax + lx * cos - ly * sin;
    };
    const ty = (px: number, py: number) => {
      const lx = px - ax;
      const ly = py - ay;
      return y + ay + lx * sin + ly * cos;
    };

    // Pack color + emission as halves, once per quad (HDR: values may exceed 1)
    const rh = toHalf(r);
    const gh = toHalf(g);
    const bh = toHalf(b);
    const ah = toHalf(a);
    const erh = toHalf(er);
    const egh = toHalf(eg);
    const ebh = toHalf(eb);
    const eah = toHalf(ea);

    // Vertex 0: top-left
    let i = baseIndex;
    const v0x = tx(0, 0);
    const v0y = ty(0, 0);
    data[i++] = v0x;
    data[i++] = v0y;
    data[i++] = u0;
    data[i++] = v0;
    i = this.writeColorEmission(i, rh, gh, bh, ah, erh, egh, ebh, eah);

    // Vertex 1: top-right
    const v1x = tx(w, 0);
    const v1y = ty(w, 0);
    data[i++] = v1x;
    data[i++] = v1y;
    data[i++] = u1;
    data[i++] = v0;
    i = this.writeColorEmission(i, rh, gh, bh, ah, erh, egh, ebh, eah);

    // Vertex 2: bottom-right
    const v2x = tx(w, h);
    const v2y = ty(w, h);
    data[i++] = v2x;
    data[i++] = v2y;
    data[i++] = u1;
    data[i++] = v1;
    i = this.writeColorEmission(i, rh, gh, bh, ah, erh, egh, ebh, eah);

    // Vertex 3: bottom-left
    const v3x = tx(0, h);
    const v3y = ty(0, h);
    data[i++] = v3x;
    data[i++] = v3y;
    data[i++] = u0;
    data[i++] = v1;
    this.writeColorEmission(i, rh, gh, bh, ah, erh, egh, ebh, eah);

    this.spriteCount++;
  }
  
  /**
   * Write a quad with custom vertex positions
   */
  private writeQuadVerts(
    p0x: number, p0y: number,
    p1x: number, p1y: number,
    p2x: number, p2y: number,
    p3x: number, p3y: number,
    u0: number,
    v0: number,
    u1: number,
    v1: number,
    r: number,
    g: number,
    b: number,
    a: number,
    er = 0,
    eg = 0,
    eb = 0,
    ea = 0
  ): void {
    const floatsPerVertex = SPRITE_VERTEX_SIZE / 4;
    const baseIndex = this.spriteCount * VERTICES_PER_SPRITE * floatsPerVertex;
    const data = this.vertexData;

    // Pack color + emission as halves, once per quad (HDR: values may exceed 1)
    const rh = toHalf(r);
    const gh = toHalf(g);
    const bh = toHalf(b);
    const ah = toHalf(a);
    const erh = toHalf(er);
    const egh = toHalf(eg);
    const ebh = toHalf(eb);
    const eah = toHalf(ea);

    // Vertex 0: top-left
    let i = baseIndex;
    data[i++] = p0x;
    data[i++] = p0y;
    data[i++] = u0;
    data[i++] = v0;
    i = this.writeColorEmission(i, rh, gh, bh, ah, erh, egh, ebh, eah);

    // Vertex 1: top-right
    data[i++] = p1x;
    data[i++] = p1y;
    data[i++] = u1;
    data[i++] = v0;
    i = this.writeColorEmission(i, rh, gh, bh, ah, erh, egh, ebh, eah);

    // Vertex 2: bottom-right
    data[i++] = p2x;
    data[i++] = p2y;
    data[i++] = u1;
    data[i++] = v1;
    i = this.writeColorEmission(i, rh, gh, bh, ah, erh, egh, ebh, eah);

    // Vertex 3: bottom-left
    data[i++] = p3x;
    data[i++] = p3y;
    data[i++] = u0;
    data[i++] = v1;
    this.writeColorEmission(i, rh, gh, bh, ah, erh, egh, ebh, eah);

    this.spriteCount++;
  }
  
  /**
   * Get number of draw calls in last batch
   */
  get drawCalls(): number {
    return this._drawCalls;
  }
  
  /**
   * Get number of sprites drawn in last batch
   */
  get totalSprites(): number {
    return this._spriteCount;
  }
  
  /**
   * Lazy-initialize GBuffer (deferred) pipeline.
   * Created on first beginGBuffer() call, using sprite-lit shader with 3 MRT outputs.
   */
  private initGBufferPipeline(): void {
    const { ctx } = this;

    this.shaderGBuffer = ctx.createShader(getCompiledShader('sprite-lit'), getCompiledShader('sprite-lit'));

    this.pipelineGBuffer = ctx.createPipeline({
      shader: this.shaderGBuffer,
      vertexBuffers: [SPRITE_VERTEX_LAYOUT],
      colorTargets: [
        { format: 'rgba16float' },   // RT0: Albedo (HDR)
        { format: 'rgba16float' },   // RT1: Normal
        { format: 'rgba16float' },   // RT2: Emission (HDR)
      ],
      depthStencil: {
        format: 'depth24plus',
        depthWrite: true,
        depthCompare: 'less-equal',
      },
      fragmentEntryPoint: 'fs_deferred',
      label: 'SpriteBatch-gbuffer',
    });
  }

  /**
   * Lazy-initialize forward-lit pipelines (LDR + HDR).
   * Created on first beginForwardLit() call, using sprite-lit shader
   * with inline lighting in the fragment shader.
   */
  private initForwardLitPipeline(): void {
    const { ctx } = this;

    // Reuse GBuffer shader module (same source, different entry point)
    if (this.shaderForwardLit === NULL_HANDLE) {
      this.shaderForwardLit = ctx.createShader(getCompiledShader('sprite-lit'), getCompiledShader('sprite-lit'));
    }

    // LDR pipeline (swap chain format)
    this.pipelineForwardLitLDR = ctx.createPipeline({
      shader: this.shaderForwardLit,
      vertexBuffers: [SPRITE_VERTEX_LAYOUT],
      colorTargets: [{ format: ctx.getSwapChainFormat() }],
      fragmentEntryPoint: 'fs_forward',
      hasLighting: true,
      label: 'SpriteBatch-forward-lit-ldr',
    });

    // HDR pipeline (rgba16float)
    this.pipelineForwardLitHDR = ctx.createPipeline({
      shader: this.shaderForwardLit,
      vertexBuffers: [SPRITE_VERTEX_LAYOUT],
      colorTargets: [{ format: 'rgba16float' }],
      fragmentEntryPoint: 'fs_forward',
      hasLighting: true,
      label: 'SpriteBatch-forward-lit-hdr',
    });
  }

  /**
   * Lazy-initialize Forward+ pipelines (LDR + HDR).
   * Created on first beginForwardPlus() call, using sprite-lit shader
   * with per-tile light culling in the fragment shader.
   */
  private initForwardPlusPipeline(): void {
    const { ctx } = this;

    // Reuse or create the lit shader module (same source, different entry point)
    if (this.shaderForwardLit === NULL_HANDLE) {
      this.shaderForwardLit = ctx.createShader(getCompiledShader('sprite-lit'), getCompiledShader('sprite-lit'));
    }

    // LDR pipeline (swap chain format)
    this.pipelineForwardPlusLDR = ctx.createPipeline({
      shader: this.shaderForwardLit,
      vertexBuffers: [SPRITE_VERTEX_LAYOUT],
      colorTargets: [{ format: ctx.getSwapChainFormat() }],
      fragmentEntryPoint: 'fs_forward_plus',
      hasForwardPlus: true,
      // Forward+ config (tileSize/maxLights/shadowRes/pcf/iesRes) is now read from uLighting.fpConfig
      // (uniform), not pipeline overrides — Slang folds override-exprs into illegal WGSL const. See sprite-lit.slang.
      label: 'SpriteBatch-forward-plus-ldr',
    });

    // HDR pipeline (rgba16float)
    this.pipelineForwardPlusHDR = ctx.createPipeline({
      shader: this.shaderForwardLit,
      vertexBuffers: [SPRITE_VERTEX_LAYOUT],
      colorTargets: [{ format: 'rgba16float' }],
      fragmentEntryPoint: 'fs_forward_plus',
      hasForwardPlus: true,
      label: 'SpriteBatch-forward-plus-hdr',
    });
  }

  /**
   * Destroy resources
   */
  destroy(): void {
    this.ctx.destroyBuffer(this.vertexBuffer);
    this.ctx.destroyBuffer(this.indexBuffer);
    if (this.pipelineHDR != NULL_HANDLE) this.ctx.destroyPipeline(this.pipelineHDR);
    if (this.pipelineLDR != NULL_HANDLE) this.ctx.destroyPipeline(this.pipelineLDR);
    if (this.pipelineGBuffer != NULL_HANDLE) this.ctx.destroyPipeline(this.pipelineGBuffer);
    if (this.pipelineForwardLitLDR != NULL_HANDLE) this.ctx.destroyPipeline(this.pipelineForwardLitLDR);
    if (this.pipelineForwardLitHDR != NULL_HANDLE) this.ctx.destroyPipeline(this.pipelineForwardLitHDR);
    if (this.pipelineForwardPlusLDR != NULL_HANDLE) this.ctx.destroyPipeline(this.pipelineForwardPlusLDR);
    if (this.pipelineForwardPlusHDR != NULL_HANDLE) this.ctx.destroyPipeline(this.pipelineForwardPlusHDR);
    this.ctx.destroyShader(this.shader);
    if (this.shaderGBuffer != NULL_HANDLE) this.ctx.destroyShader(this.shaderGBuffer);
    if (this.shaderForwardLit != NULL_HANDLE) this.ctx.destroyShader(this.shaderForwardLit);
  }
}
